/**
 * Pollinations API 调用封装 Hook
 * 支持 BYOP 用户授权模式
 */

import { useState, useCallback } from 'react'

const BASE_URL = 'https://gen.pollinations.ai'
const OAUTH_BASE = 'https://enter.pollinations.ai'

/**
 * 生成 PKCE 参数
 */
function generatePKCE() {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)).then((digest) => {
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
    return { verifier, challenge }
  })
}

/**
 * BYOP 认证 Hook
 */
export function useBYOPAuth(appKey) {
  const [userKey, setUserKey] = useState(() => localStorage.getItem('pollinations_user_key'))
  const [userInfo, setUserInfo] = useState(() => {
    const saved = localStorage.getItem('pollinations_user_info')
    return saved ? JSON.parse(saved) : null
  })
  const [loading, setLoading] = useState(false)

  const initiateAuth = useCallback(async () => {
    if (!appKey) {
      alert('请先配置 App Key (pk_...)')
      return
    }
    setLoading(true)
    try {
      const { verifier, challenge } = await generatePKCE()
      localStorage.setItem('pollinations_pkce_verifier', verifier)

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: appKey,
        redirect_uri: window.location.origin + window.location.pathname,
        scope: 'profile usage',
        state: Math.random().toString(36).substring(2),
        code_challenge: challenge,
        code_challenge_method: 'S256',
      })

      window.location.href = `${OAUTH_BASE}/authorize?${params.toString()}`
    } catch (err) {
      console.error('Auth initiation failed:', err)
      setLoading(false)
    }
  }, [appKey])

  const handleCallback = useCallback(async () => {
    const hash = new URLSearchParams(window.location.hash.slice(1))
    const query = new URLSearchParams(window.location.search)
    const code = query.get('code') || hash.get('code')
    const state = query.get('state') || hash.get('state')
    const verifier = localStorage.getItem('pollinations_pkce_verifier')

    if (!code || !verifier) return false

    setLoading(true)
    try {
      const res = await fetch(`${OAUTH_BASE}/api/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          client_id: appKey,
          redirect_uri: window.location.origin + window.location.pathname,
          code_verifier: verifier,
        }),
      })

      const data = await res.json()
      if (data.access_token) {
        localStorage.setItem('pollinations_user_key', data.access_token)
        setUserKey(data.access_token)
        localStorage.removeItem('pollinations_pkce_verifier')

        // 获取用户信息
        const userRes = await fetch(`${OAUTH_BASE}/api/oauth/userinfo`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
        })
        if (userRes.ok) {
          const userData = await userRes.json()
          localStorage.setItem('pollinations_user_info', JSON.stringify(userData))
          setUserInfo(userData)
        }

        // 清理 URL
        window.history.replaceState({}, document.title, window.location.pathname)
        return true
      }
    } catch (err) {
      console.error('Token exchange failed:', err)
    } finally {
      setLoading(false)
    }
    return false
  }, [appKey])

  const logout = useCallback(() => {
    localStorage.removeItem('pollinations_user_key')
    localStorage.removeItem('pollinations_user_info')
    setUserKey(null)
    setUserInfo(null)
  }, [])

  return { userKey, userInfo, loading, initiateAuth, handleCallback, logout }
}

/**
 * 获取当前有效的 API Key
 * 优先使用用户授权的 BYOP key，否则使用开发者 key
 */
function getEffectiveKey(devKey) {
  return localStorage.getItem('pollinations_user_key') || devKey
}

/**
 * 音频转录 Hook
 */
export function useTranscription(devKey) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const transcribe = useCallback(
    async (audioFile, language = 'auto') => {
      const key = getEffectiveKey(devKey)
      if (!key) {
        setError('缺少 API Key，请先登录或配置开发者 Key')
        return null
      }

      setLoading(true)
      setProgress('正在上传音频并转录...')
      setError(null)

      try {
        const formData = new FormData()
        formData.append('file', audioFile)
        formData.append('model', 'openai-audio')
        formData.append('response_format', 'verbose_json')
        if (language !== 'auto') {
          formData.append('language', language)
        }

        const res = await fetch(`${BASE_URL}/v1/audio/transcriptions`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${key}` },
          body: formData,
        })

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}))
          throw new Error(errData.error?.message || `转录失败: ${res.status}`)
        }

        const data = await res.json()
        setResult(data)
        return data
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [devKey]
  )

  return { transcribe, loading, progress, result, error, setResult }
}

/**
 * 文本翻译 Hook
 */
export function useTranslation(devKey) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const translate = useCallback(
    async (texts, targetLang, sourceLang = 'auto') => {
      const key = getEffectiveKey(devKey)
      if (!key) {
        setError('缺少 API Key，请先登录或配置开发者 Key')
        return null
      }

      setLoading(true)
      setProgress('正在翻译字幕...')
      setError(null)

      try {
        const prompt = `请将以下${sourceLang !== 'auto' ? `从 ${sourceLang} ` : ''}字幕文本翻译成 ${targetLang}。
要求：
1. 保持原有的时间戳和分段结构
2. 翻译要自然流畅，符合口语表达习惯
3. 只返回翻译后的文本，每行对应一段字幕
4. 不要添加任何解释或额外内容

字幕文本：
${texts.map((t, i) => `[${i + 1}] ${t}`).join('\n')}`

        const res = await fetch(`${BASE_URL}/v1/chat/completions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${key}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'openai',
            messages: [
              {
                role: 'system',
                content: '你是一位专业的视频字幕翻译专家，擅长将口语化内容翻译成地道、自然的目标语言。',
              },
              { role: 'user', content: prompt },
            ],
            temperature: 0.3,
          }),
        })

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}))
          throw new Error(errData.error?.message || `翻译失败: ${res.status}`)
        }

        const data = await res.json()
        const translatedText = data.choices[0].message.content

        // 解析返回的翻译结果
        const lines = translatedText.trim().split('\n')
        const translated = lines
          .map((line) => {
            const match = line.match(/^\[\d+\]\s*(.*)$/)
            return match ? match[1].trim() : line.trim()
          })
          .filter((l) => l)

        setResult(translated)
        return translated
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [devKey]
  )

  return { translate, loading, progress, result, error, setResult }
}

/**
 * 语音合成 Hook (使用 Audio GET API)
 */
export function useTTS(devKey) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [audioUrl, setAudioUrl] = useState(null)
  const [error, setError] = useState(null)

  const synthesize = useCallback(
    async (text, voice = 'nova', language = 'zh') => {
      const key = getEffectiveKey(devKey)
      if (!key) {
        setError('缺少 API Key，请先登录或配置开发者 Key')
        return null
      }

      setLoading(true)
      setProgress('正在合成语音...')
      setError(null)

      try {
        // 文本需要 URL 编码
        const encodedText = encodeURIComponent(text)
        const url = `${BASE_URL}/audio/${encodedText}?voice=${voice}&lang=${language}&key=${key}`

        const res = await fetch(url)
        if (!res.ok) {
          throw new Error(`语音合成失败: ${res.status}`)
        }

        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        setAudioUrl(objectUrl)
        return objectUrl
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [devKey]
  )

  return { synthesize, loading, progress, audioUrl, error, setAudioUrl }
}

/**
 * 批量语音合成（用于整段字幕）
 */
export function useBatchTTS(devKey) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [audioUrls, setAudioUrls] = useState([])
  const [error, setError] = useState(null)

  const synthesizeBatch = useCallback(
    async (segments, voice = 'nova', language = 'zh') => {
      const key = getEffectiveKey(devKey)
      if (!key) {
        setError('缺少 API Key，请先登录或配置开发者 Key')
        return null
      }

      setLoading(true)
      setError(null)
      const urls = []

      try {
        for (let i = 0; i < segments.length; i++) {
          setProgress(`正在合成语音... (${i + 1}/${segments.length})`)
          const text = segments[i].text
          if (!text.trim()) {
            urls.push(null)
            continue
          }

          const encodedText = encodeURIComponent(text)
          const url = `${BASE_URL}/audio/${encodedText}?voice=${voice}&lang=${language}&key=${key}`

          const res = await fetch(url)
          if (!res.ok) {
            throw new Error(`第 ${i + 1} 段语音合成失败: ${res.status}`)
          }

          const blob = await res.blob()
          urls.push(URL.createObjectURL(blob))

          // 简单限速，避免请求过快
          if (i < segments.length - 1) {
            await new Promise((r) => setTimeout(r, 300))
          }
        }

        setAudioUrls(urls)
        return urls
      } catch (err) {
        setError(err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [devKey]
  )

  return { synthesizeBatch, loading, progress, audioUrls, error, setAudioUrls }
}